import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Helmet } from "react-helmet";
import RobotSelection from '../Common/RobotSelection';
import FolderSelection from '../Common/FolderSelection';
import config from '../../config/config';
import '../../styles/Triggers.css';
import ProcessSelection from '../Common/ProcessSelection';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const EditLinkedTaskTrigger = () => {
    let { id } = useParams(); // Get trigger ID from URL

    const [completedProcess, setCompletedProcess] = useState('');
    const [nextProcess, setNextProcess] = useState('');
    const [robot, setRobot] = useState('All');
    const [name, setName] = useState('');
    const [status, setStatus] = useState('');
    const [argumentsList, setArgumentsList] = useState([]);
    const [folder, setFolder] = useState('');
    const [priority, setPriority] = useState('Inherited');
    const [stickToRobot, setStickToRobot] = useState(false);
    const navigate = useNavigate();

    const priorities = [
        { value: "Inherited", label: "Inherited", icon: "○" },
        { value: "Critical", label: "Critical", icon: "◊" },
        { value: "Highest", label: "Highest", icon: "≫" },
        { value: "Very high", label: "Very high", icon: "≫" },
        { value: "High", label: "High", icon: "∧" },
        { value: "Medium-high", label: "Medium-high", icon: "⋀" },
        { value: "Medium", label: "Medium", icon: "=" },
        { value: "Medium-low", label: "Medium-low", icon: "⋁" },
        { value: "Low", label: "Low", icon: "∨" },
        { value: "Very low", label: "Very low", icon: "≪" },
        { value: "Lowest", label: "Lowest", icon: "≪" }
    ];

    const fetchLinkedTaskTrigger = async () => {
        try {
            const token = localStorage.getItem('userToken');
            const response = await axios.get(`${config.API_BASE_URL}/api/linkedtaskTrigger/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            console.log('Response data:', response.data);

            setCompletedProcess({
                id: response.data.completed_process_key,  // Use process key as id
                name: response.data.completed_process_name,
                process_key: response.data.completed_process_key  // Store process key separately
            });

            setNextProcess({
                id: response.data.next_process_key,  // Use process key as id
                name: response.data.next_process_name,
                process_key: response.data.next_process_key  // Store process key separately
            });

            setRobot(response.data.robot_id || 'All');
            setName(response.data.name || '');
            setStatus(response.data.status || '');
            setFolder(response.data.folder_id || '');
            setArgumentsList(response.data.argumentsList || []);
            setPriority(response.data.priority || 'Inherited');
            setStickToRobot(Boolean(response.data.stickto_robot));
        } catch (error) {
            toast.error("Failed to load trigger details. Please try again.");
            console.error('Error fetching linked task triggers:', error);
        }
    };

    const handleLinkedTaskTrigger = async () => {
        try {
            const token = localStorage.getItem('userToken');
            const payload = {
                name,
                status,
                completedProcess: {
                    id: completedProcess.id || completedProcess.process_key,  // Use process key
                    name: completedProcess.name
                },
                nextProcess: {
                    id: nextProcess.id || nextProcess.process_key,  // Use process key
                    name: nextProcess.name,
                    stickto_robot: stickToRobot
                },
                argumentsList, // Move argumentsList to root
                robot,
                folderId: folder,
                priority: priority
            };

            await axios.put(`${config.API_BASE_URL}/api/linkedTaskTrigger/${id}`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });

            toast.success("Linked Task Trigger updated successfully!");
            navigate('/dashboard');
        } catch (error) {
            toast.error("Failed to update trigger. Please try again.");
            console.error('Error updating trigger:', error);
        }
    };

    const handleCompleteProcessChange = (selected) => {
        if (selected) {
            setCompletedProcess({
                id: selected.ProcessKey || selected.Key || selected.id,
                dbId: selected.id,  // Store the database ID
                name: selected.Name || selected.ProcessKey || selected.name
            });
        } else {
            setCompletedProcess({ id: "", dbId: "", name: "" });
        }
    };

    useEffect(() => {
        fetchLinkedTaskTrigger();
    }, []);

    return (
        <div className="trigger-container">
            <Helmet>
                <title>Edit Linked Task Trigger</title>
            </Helmet>
            <h2>Edit Linked Task Trigger</h2>
            <div className="form-group">
                <label>Trigger Name *</label>
                <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter Trigger Name"
                />
            </div>

            <FolderSelection
                folder={folder}
                onChange={setFolder}
                isGlobal={true}
            />

            <ProcessSelection
                process={completedProcess}
                onChange={handleCompleteProcessChange}
                label="Completed Process *"
                folder={folder}
            />

            <ProcessSelection
                process={nextProcess}
                onChange={(selected) => {
                    if (selected) {
                        setNextProcess({
                            id: selected.ProcessKey || selected.Key || selected.id,
                            dbId: selected.id,  // Store the database ID
                            name: selected.Name || selected.ProcessKey || selected.name
                        });
                    } else {
                        setNextProcess({ id: "", dbId: "", name: "" });
                    }
                }}
                label="Next Process *"
                disabledProcess={completedProcess}
                folder={folder}
            />

            <div className="form-group process-robot-lock">
                <div className="robot-lock-container">
                    <input
                        type="checkbox"
                        id="stickToRobot"
                        checked={stickToRobot}
                        onChange={(e) => setStickToRobot(e.target.checked)}
                        disabled={robot === 'All'}
                    />
                    <label htmlFor="stickToRobot" className="robot-lock-label">
                        <span className="lock-icon">🔒</span>
                        Lock next process to selected robot
                    </label>
                </div>
                {robot === 'All' ? (
                    <small className="help-text warning">
                        Select a specific robot to enable process locking
                    </small>
                ) : (
                    <small className="help-text">
                        The next process will only be executed by the selected robot
                    </small>
                )}
            </div>

            {nextProcess.id && (
                <div className="form-group">
                    <label>Input Arguments for Next Process</label>
                    <button
                        onClick={() => setArgumentsList([...argumentsList, { name: "", value: "" }])}
                        type="button"
                    >
                        Add Argument
                    </button>
                    {argumentsList.map((arg, index) => (
                        <div key={index} className="argument">
                            <input
                                type="text"
                                value={arg.name}
                                onChange={(e) => setArgumentsList(argumentsList.map((a, i) =>
                                    i === index ? { ...a, name: e.target.value } : a
                                ))}
                                placeholder="Argument Name"
                            />
                            <input
                                type="text"
                                value={arg.value}
                                onChange={(e) => setArgumentsList(argumentsList.map((a, i) =>
                                    i === index ? { ...a, value: e.target.value } : a
                                ))}
                                placeholder="Argument Value"
                            />
                            <button
                                onClick={() => setArgumentsList(argumentsList.filter((_, i) => i !== index))}
                                type="button"
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <div className="form-group">
                <label>Job Priority</label>
                <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    className="form-control"
                >
                    {priorities.map((p) => (
                        <option key={p.value} value={p.value}>
                            {p.icon} {p.label}
                        </option>
                    ))}
                </select>
            </div>

            <button onClick={handleLinkedTaskTrigger}>Save Trigger</button>
        </div>
    )
}

export default EditLinkedTaskTrigger